import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Loader2, Eye, EyeOff, Fingerprint, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { isWebAuthnSupported, authenticateWithPasskey } from '../lib/webauthn';
import { consumeLogoutReason } from '../lib/authStorage';
import { useCompanyBranding, resolveLogoUrl } from '../hooks/useCompanyBranding';

const LOGOUT_MESSAGES = {
  inactivity: 'Du wurdest wegen Inaktivität automatisch abgemeldet. Bitte erneut anmelden.',
  expired: 'Deine Sitzung ist abgelaufen. Bitte erneut anmelden.',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [logoutNotice, setLogoutNotice] = useState('');
  const [loginError, setLoginError] = useState('');
  const branding = useCompanyBranding();
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const webAuthnAvailable = isWebAuthnSupported();

  useEffect(() => {
    const reason = consumeLogoutReason();
    if (reason && LOGOUT_MESSAGES[reason]) {
      setLogoutNotice(LOGOUT_MESSAGES[reason]);
    }
  }, []);

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const data = await authenticateWithPasskey();
      loginWithToken(data.access_token, data.user, remember);
      toast.success('Erfolgreich per Passkey angemeldet');
      navigate('/dashboard');
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // User hat abgebrochen – keine Fehlermeldung
        return;
      }
      const detail = err?.response?.data?.detail || err?.message || 'Passkey-Anmeldung fehlgeschlagen';
      toast.error(detail);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!email || !password) {
      setLoginError('Bitte alle Felder ausfüllen');
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success('Erfolgreich angemeldet');
      navigate('/dashboard');
    } catch (err) {
      let msg = err.message || 'Anmeldung fehlgeschlagen';
      if (msg === 'Invalid credentials') {
        msg = 'E-Mail oder Passwort ist falsch.';
      }
      setLoginError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo & Company Name */}
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <div className="mb-4">
              <img
                src={resolveLogoUrl(branding.logo_url)}
                alt={branding.name}
                className="h-16 w-auto mx-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="hidden items-center justify-center w-16 h-16 rounded-2xl mx-auto"
                style={{ backgroundColor: branding.primary_color }}
              >
                <Users className="h-8 w-8 text-white" />
              </div>
            </div>
          ) : (
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{ backgroundColor: branding.primary_color }}
            >
              <Users className="h-8 w-8 text-white" />
            </div>
          )}
          <h1 
            className="font-heading font-bold text-3xl"
            style={{ color: branding.primary_color }}
            data-testid="company-name"
          >
            {branding.name}
          </h1>
          <p className="text-muted-foreground mt-1">Mitarbeiterverwaltung</p>
        </div>

        <Card className="border-border/60 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl">Anmelden</CardTitle>
            <CardDescription>
              Melden Sie sich mit Ihren Zugangsdaten an
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logoutNotice && (
              <div
                className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="logout-notice"
              >
                <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{logoutNotice}</span>
              </div>
            )}
            {loginError && (
              <div
                className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                data-testid="login-error"
                role="alert"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{loginError}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@firma.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(v === true)}
                  data-testid="login-remember-checkbox"
                />
                <Label htmlFor="remember" className="text-sm font-normal cursor-pointer select-none">
                  Angemeldet bleiben
                </Label>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Aktiviert: auf diesem Gerät angemeldet bleiben. Deaktiviert: Abmeldung beim Schließen des Browsers (empfohlen an fremden Geräten).
              </p>

              <Button
                type="submit"
                className="w-full h-11 rounded-full font-semibold"
                disabled={loading}
                data-testid="login-submit-btn"
                style={{ 
                  backgroundColor: branding.primary_color,
                  borderColor: branding.primary_color 
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird angemeldet...
                  </>
                ) : (
                  'Anmelden'
                )}
              </Button>

              {webAuthnAvailable && (
                <>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">oder</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 rounded-full font-semibold gap-2"
                    onClick={handlePasskeyLogin}
                    disabled={passkeyLoading || loading}
                    data-testid="login-passkey-btn"
                  >
                    {passkeyLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Fingerprint className="h-4 w-4" />
                    )}
                    Mit Fingerabdruck anmelden
                  </Button>
                </>
              )}

              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => toast.info('Bitte kontaktieren Sie Ihren Administrator, um Ihr Passwort zurückzusetzen.', { duration: 5000 })}
                  className="text-sm text-muted-foreground hover:underline"
                  data-testid="forgot-password-link"
                >
                  Passwort vergessen?
                </button>
              </div>
            </form>

            
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          © {new Date().getFullYear()} {branding.name}. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
}
